import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Bestaetigung } from '../components/Bestaetigung';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DEMO, api, apiFetch, ApiFehler } from '../lib/api';
import { formatDatum } from '../lib/format';
import { demoHaus } from '../lib/haus';
import { transition, uebergangSanft } from '../lib/motion';
import type { ApiObjektVoll } from './ObjektFormular';

/**
 * Objekt entfernen, archivieren, wiederherstellen und endgültig löschen.
 *
 * Regeln (Server, routes/objekte.ts):
 *   * Ohne aufbewahrungspflichtige Unterlagen wird das Objekt gelöscht — mit Einheiten,
 *     Mietverhältnissen, Zählern und den zugehörigen Mieterdaten.
 *   * Mit Unterlagen (Abrechnungen, Belege, Heizkostenabrechnungen, Zahlungen, Mahnungen,
 *     Prüfprotokolle) wird archiviert; das Archiv kann wiederhergestellt werden.
 *   * Endgültig löschen nur mit Eingabe der Objektbezeichnung und ohne festgeschriebene
 *     Abrechnung (GoBD).
 */

export interface Objektbestand {
  einheiten: number;
  mietverhaeltnisse: number;
  zaehler: number;
  abrechnungen: number;
  abrechnungenFestgeschrieben: number;
  belege: number;
  heizkostenabrechnungen: number;
  zahlungen: number;
  mahnungen: number;
  pruefprotokolle: number;
  dokumente: number;
}

export interface Loeschpruefung {
  bezeichnung: string;
  archiviert: boolean;
  bestand: Objektbestand;
  modus: 'loeschen' | 'archivieren';
  endgueltigMoeglich: boolean;
}

/* ------------------------------------------------------------------ Vorschau (ohne Server) */

/** Zustand der Vorschau: entfernte und archivierte Objekte dieser Sitzung. */
export const demoObjektZustand = new Map<string, { status: 'archiviert' | 'geloescht'; objekt: ApiObjektVoll; am: string }>();

const DEMO_UNTERLAGEN: Record<string, Partial<Objektbestand>> = {
  'obj-1': { abrechnungen: 2, abrechnungenFestgeschrieben: 1, belege: 18, zahlungen: 46, heizkostenabrechnungen: 1 },
  'obj-2': { abrechnungen: 1, belege: 7, zahlungen: 12 },
};

function demoPruefung(o: ApiObjektVoll): Loeschpruefung {
  const h = demoHaus(o.id);
  const u = DEMO_UNTERLAGEN[o.id] ?? {};
  const bestand: Objektbestand = {
    einheiten: h?.einheiten.length ?? o.einheitenAnzahl ?? 0,
    mietverhaeltnisse: h?.einheiten.filter((e) => e.belegung.status === 'vermietet').length ?? 0,
    zaehler: h?.einheiten.reduce((s, e) => s + e.zaehlerAnzahl, 0) ?? 0,
    abrechnungen: 0,
    abrechnungenFestgeschrieben: 0,
    belege: 0,
    heizkostenabrechnungen: 0,
    zahlungen: 0,
    mahnungen: 0,
    pruefprotokolle: 0,
    dokumente: 0,
    ...u,
  };
  const unterlagen =
    bestand.abrechnungen + bestand.belege + bestand.heizkostenabrechnungen + bestand.zahlungen + bestand.mahnungen + bestand.pruefprotokolle;
  return {
    bezeichnung: o.bezeichnung,
    archiviert: demoObjektZustand.get(o.id)?.status === 'archiviert',
    bestand,
    modus: unterlagen > 0 ? 'archivieren' : 'loeschen',
    endgueltigMoeglich: bestand.abrechnungenFestgeschrieben === 0,
  };
}

/* ------------------------------------------------------------------ Texte */

function anzahl(n: number, einzahl: string, mehrzahl: string): string {
  return `${n.toLocaleString('de-DE')} ${n === 1 ? einzahl : mehrzahl}`;
}

function stammdatenListe(b: Objektbestand): string[] {
  return [
    b.einheiten > 0 ? anzahl(b.einheiten, 'Einheit', 'Einheiten') : null,
    b.mietverhaeltnisse > 0 ? `${anzahl(b.mietverhaeltnisse, 'Mietverhältnis', 'Mietverhältnisse')} mit Vertragsdaten und Mieterkontakten` : null,
    b.zaehler > 0 ? `${anzahl(b.zaehler, 'Zähler', 'Zähler')} mit allen Ablesungen` : null,
  ].filter((x): x is string => x !== null);
}

function unterlagenListe(b: Objektbestand): string[] {
  return [
    b.abrechnungen > 0 ? anzahl(b.abrechnungen, 'Abrechnung', 'Abrechnungen') : null,
    b.belege > 0 ? `${anzahl(b.belege, 'Beleg', 'Belege')} mit Belegdateien` : null,
    b.heizkostenabrechnungen > 0 ? anzahl(b.heizkostenabrechnungen, 'Heizkostenabrechnung', 'Heizkostenabrechnungen') : null,
    b.zahlungen > 0 ? `${anzahl(b.zahlungen, 'zugeordnete Zahlung', 'zugeordnete Zahlungen')}` : null,
    b.mahnungen > 0 ? anzahl(b.mahnungen, 'Mahnung', 'Mahnungen') : null,
    b.pruefprotokolle > 0 ? anzahl(b.pruefprotokolle, 'Prüfprotokoll', 'Prüfprotokolle') : null,
  ].filter((x): x is string => x !== null);
}

function Liste({ eintraege }: { eintraege: string[] }) {
  return (
    <ul className="list-disc space-y-0.5 pl-5 text-fg">
      {eintraege.map((e) => (
        <li key={e}>{e}</li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ Objekt entfernen (Stammdaten) */

export function ObjektEntfernen({
  objekt,
  onEntfernt,
}: {
  objekt: ApiObjektVoll;
  onEntfernt: (meldung: string) => void;
}) {
  const [pruefung, setPruefung] = useState<Loeschpruefung | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function oeffnen() {
    setFehler(null);
    if (DEMO) {
      setPruefung(demoPruefung(objekt));
      return;
    }
    setLaedt(true);
    try {
      setPruefung(await api.get<Loeschpruefung>(`/objekte/${objekt.id}/loeschpruefung`));
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Prüfung konnte nicht durchgeführt werden.');
    } finally {
      setLaedt(false);
    }
  }

  async function ausfuehren() {
    if (!pruefung) return;
    setSendet(true);
    setFehler(null);
    try {
      let archiviert = pruefung.modus === 'archivieren';
      if (DEMO) {
        demoObjektZustand.set(objekt.id, { status: archiviert ? 'archiviert' : 'geloescht', objekt, am: new Date().toISOString() });
      } else {
        const r = await api.del<{ geloescht?: boolean; archiviert?: boolean }>(`/objekte/${objekt.id}`);
        archiviert = Boolean(r.archiviert);
      }
      setPruefung(null);
      onEntfernt(
        archiviert
          ? `„${objekt.bezeichnung}“ wurde archiviert. Abrechnungen und Belege bleiben erhalten; das Objekt kann im Archiv wiederhergestellt werden.`
          : `„${objekt.bezeichnung}“ wurde gelöscht.`,
      );
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Das Objekt konnte nicht entfernt werden.');
    } finally {
      setSendet(false);
    }
  }

  const b = pruefung?.bestand;
  const loeschen = pruefung?.modus === 'loeschen';

  return (
    <>
      <Card titel="Objekt entfernen">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-relaxed text-fg-muted">
            Objekte ohne Abrechnungen und Belege werden vollständig gelöscht. Bestehen aufbewahrungspflichtige Unterlagen,
            wird das Objekt archiviert und kann wiederhergestellt werden.
          </p>
          <Button variante="gefahr" className="shrink-0" onClick={() => void oeffnen()} ladend={laedt}>
            Objekt entfernen
          </Button>
        </div>
        {fehler && !pruefung && <p className="mt-3 text-sm font-medium text-danger">{fehler}</p>}
      </Card>

      <Bestaetigung
        offen={pruefung !== null}
        titel={loeschen ? `„${objekt.bezeichnung}“ löschen?` : `„${objekt.bezeichnung}“ archivieren?`}
        aktion={loeschen ? 'Objekt löschen' : 'Objekt archivieren'}
        ton={loeschen ? 'gefahr' : 'primaer'}
        eingabePflicht={loeschen && b && b.mietverhaeltnisse + b.zaehler > 0 ? objekt.bezeichnung : undefined}
        ladend={sendet}
        fehler={fehler}
        onBestaetigen={() => void ausfuehren()}
        onAbbrechen={() => setPruefung(null)}
      >
        {b && loeschen && (
          <>
            {stammdatenListe(b).length > 0 ? (
              <>
                <p>Folgende Daten werden unwiderruflich gelöscht:</p>
                <Liste eintraege={stammdatenListe(b)} />
              </>
            ) : (
              <p>Zu diesem Objekt sind noch keine Einheiten erfasst. Das Objekt wird unwiderruflich gelöscht.</p>
            )}
            {b.dokumente > 0 && <p>Dokumente bleiben ohne Objektzuordnung in der Dokumentenablage erhalten.</p>}
          </>
        )}
        {b && !loeschen && (
          <>
            <p>Zu diesem Objekt bestehen aufbewahrungspflichtige Unterlagen:</p>
            <Liste eintraege={unterlagenListe(b)} />
            <p>
              Das Objekt erscheint danach nicht mehr in der Objektliste, in Auswertungen und Fristen. Alle Daten bleiben
              erhalten; im Archiv kann es wiederhergestellt
              {pruefung?.endgueltigMoeglich ? ' oder endgültig gelöscht' : ''} werden.
            </p>
          </>
        )}
      </Bestaetigung>
    </>
  );
}

/* ------------------------------------------------------------------ Archiv (Objektliste) */

export function ObjektArchiv({
  onWiederhergestellt,
  onMeldung,
}: {
  onWiederhergestellt: (o: ApiObjektVoll) => void;
  onMeldung: (text: string) => void;
}) {
  const reduziert = useReducedMotion();
  const [liste, setListe] = useState<Array<ApiObjektVoll & { archiviertAm: string | null }> | null>(null);
  const [offen, setOffen] = useState(false);
  const [aktiv, setAktiv] = useState<{ objekt: ApiObjektVoll; pruefung: Loeschpruefung } | null>(null);
  const [sendet, setSendet] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  async function laden() {
    if (DEMO) {
      setListe(
        [...demoObjektZustand.entries()]
          .filter(([, z]) => z.status === 'archiviert')
          .map(([, z]) => ({ ...z.objekt, archiviertAm: z.am })),
      );
      return;
    }
    try {
      const d = await api.get<{ objekte: Array<ApiObjektVoll & { archiviertAm: string | null }> }>('/objekte?archiviert=true');
      setListe(d.objekte.filter((o) => o.archiviertAm));
    } catch {
      setListe([]);
    }
  }

  useEffect(() => {
    void laden();
  }, []);

  if (!liste || liste.length === 0) return null;

  async function wiederherstellen(o: ApiObjektVoll) {
    setSendet(o.id);
    setFehler(null);
    try {
      if (DEMO) demoObjektZustand.delete(o.id);
      else await api.post(`/objekte/${o.id}/wiederherstellen`);
      setListe((l) => (l ?? []).filter((x) => x.id !== o.id));
      onWiederhergestellt(o);
      onMeldung(`„${o.bezeichnung}“ wurde wiederhergestellt.`);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Das Objekt konnte nicht wiederhergestellt werden.');
    } finally {
      setSendet(null);
    }
  }

  async function endgueltigPruefen(o: ApiObjektVoll) {
    setFehler(null);
    try {
      const p = DEMO ? demoPruefung(o) : await api.get<Loeschpruefung>(`/objekte/${o.id}/loeschpruefung`);
      setAktiv({ objekt: o, pruefung: p });
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Prüfung konnte nicht durchgeführt werden.');
    }
  }

  async function endgueltigLoeschen(bestaetigung: string) {
    if (!aktiv) return;
    setSendet(aktiv.objekt.id);
    setFehler(null);
    try {
      if (DEMO) demoObjektZustand.set(aktiv.objekt.id, { status: 'geloescht', objekt: aktiv.objekt, am: new Date().toISOString() });
      else await apiLoeschenMitBestaetigung(aktiv.objekt.id, bestaetigung);
      const name = aktiv.objekt.bezeichnung;
      setListe((l) => (l ?? []).filter((x) => x.id !== aktiv.objekt.id));
      setAktiv(null);
      onMeldung(`„${name}“ wurde endgültig gelöscht.`);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Das Objekt konnte nicht gelöscht werden.');
    } finally {
      setSendet(null);
    }
  }

  const p = aktiv?.pruefung;

  return (
    <section className="mt-6">
      <button
        type="button"
        onClick={() => setOffen((x) => !x)}
        aria-expanded={offen}
        className="inline-flex items-center gap-2 rounded-md text-sm font-medium text-fg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        <motion.span animate={{ rotate: offen ? 90 : 0 }} transition={transition(!!reduziert, uebergangSanft)} aria-hidden="true">
          ›
        </motion.span>
        Archivierte Objekte ({liste.length})
      </button>
      <AnimatePresence initial={false}>
        {offen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={transition(!!reduziert, uebergangSanft)}
            className="overflow-hidden"
          >
            <Card className="mt-3">
              {fehler && !aktiv && <p className="mb-3 text-sm font-medium text-danger">{fehler}</p>}
              <ul className="divide-y divide-border">
                {liste.map((o) => (
                  <li key={o.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-fg">{o.bezeichnung}</p>
                      <p className="truncate text-xs text-fg-muted">
                        {o.anschrift}
                        {o.archiviertAm ? ` · archiviert am ${formatDatum(o.archiviertAm.slice(0, 10))}` : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button groesse="sm" variante="sekundaer" ladend={sendet === o.id && !aktiv} onClick={() => void wiederherstellen(o)}>
                        Wiederherstellen
                      </Button>
                      <Button groesse="sm" variante="gefahr" onClick={() => void endgueltigPruefen(o)}>
                        Endgültig löschen
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {aktiv && p && !p.endgueltigMoeglich ? (
        <Bestaetigung
          offen
          titel="Endgültiges Löschen nicht möglich"
          aktion="Verstanden"
          ton="primaer"
          nurHinweis
          onBestaetigen={() => setAktiv(null)}
          onAbbrechen={() => setAktiv(null)}
        >
          <p>
            Zu „{aktiv.objekt.bezeichnung}“ bestehen {anzahl(p.bestand.abrechnungenFestgeschrieben, 'festgeschriebene oder versendete Abrechnung', 'festgeschriebene oder versendete Abrechnungen')}.
            Diese sind unveränderbar und unterliegen der Aufbewahrungspflicht. Das Objekt bleibt im Archiv; es belastet weder
            Objektliste noch Auswertungen.
          </p>
        </Bestaetigung>
      ) : (
        <Bestaetigung
          offen={aktiv !== null}
          titel={aktiv ? `„${aktiv.objekt.bezeichnung}“ endgültig löschen?` : ''}
          aktion="Endgültig löschen"
          eingabePflicht={aktiv?.objekt.bezeichnung}
          ladend={sendet !== null}
          fehler={fehler}
          onBestaetigen={() => aktiv && void endgueltigLoeschen(aktiv.objekt.bezeichnung)}
          onAbbrechen={() => setAktiv(null)}
        >
          {p && (
            <>
              <p>Das Objekt wird mit allen Daten unwiderruflich gelöscht:</p>
              <Liste eintraege={[...stammdatenListe(p.bestand), ...unterlagenListe(p.bestand)]} />
              <p>
                Steuerlich relevante Belege unterliegen gesetzlichen Aufbewahrungsfristen. Erstellen Sie bei Bedarf vorher
                einen Datenexport (Einstellungen → Datenexport).
              </p>
            </>
          )}
        </Bestaetigung>
      )}
    </section>
  );
}

async function apiLoeschenMitBestaetigung(id: string, bestaetigung: string): Promise<void> {
  await apiFetch(`/objekte/${id}/endgueltig`, { method: 'DELETE', body: { bestaetigung } });
}
